/**
 * KAN-204 Ship C — per-USER "already greeted" receipts for the company-access
 * sign-in card. Same adapter shape as `../notices/securityAlertReceipts.js`:
 * a local map (fast cache) + write-through via `upsertNoticeReceipts` +
 * MONOTONIC merge from the server (`NoticeReceiptsSync`), so a reload or a
 * second device never shows the same card twice.
 *
 * notice_key = `company_access:<level>:<reason>:<next_step_on|none>` — levels,
 * reasons and a company-local date ONLY, never a name or an email. A new
 * state change is a NEW key and greets again. The local cache is scoped per
 * user AND tenant (a shared office device must not cross-mark another
 * company). `announced_at` is the moment this person was shown the card.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';
import { normalizeLockReason, normalizeNextStepOn } from './companyAccessCopy';

const PREFIX = 'company_access:';
const MAX_ENTRIES = 50;
export const COMPANY_ACCESS_GREETED_PREFIX = 'cvx-company-access-greeted-v1:';

/** The server notice_key for a (level, reason, next_step_on) state. */
export function companyAccessNoticeKey(level, reason, nextStepOn) {
  return `${PREFIX}${level}:${normalizeLockReason(reason)}:${normalizeNextStepOn(nextStepOn) || 'none'}`;
}

export function ownsCompanyAccessNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(PREFIX);
}

export function companyAccessGreetedKey(userId, tenantId) {
  return `${COMPANY_ACCESS_GREETED_PREFIX}${tenantId || 'tenant'}:${userId || 'anon'}`;
}

const listeners = new Set();
/** Subscribe to receipt changes (a merge from another device, or our own mark). */
export function subscribeCompanyAccessGreeted(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (_e) { /* never break the others */ } });
}

function capNewest(map) {
  const keep = Object.keys(map).sort((a, b) => Date.parse(map[b]) - Date.parse(map[a])).slice(0, MAX_ENTRIES);
  const out = {};
  keep.forEach((k) => { out[k] = map[k]; });
  return out;
}

/** `{ noticeKey: isoInstant }`. No user id -> nothing, ever. Unreadable -> empty. */
export function readCompanyAccessGreeted(userId, tenantId, storage) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(companyAccessGreetedKey(userId, tenantId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((k) => {
      if (!ownsCompanyAccessNoticeKey(k) || Number.isNaN(Date.parse(parsed[k]))) return;
      out[k] = new Date(Date.parse(parsed[k])).toISOString();
    });
    return out;
  } catch {
    return {};
  }
}

export function hasGreetedCompanyAccess(userId, tenantId, noticeKey, storage) {
  return Boolean(readCompanyAccessGreeted(userId, tenantId, storage)[noticeKey]);
}

function writeMap(userId, tenantId, next, storage) {
  try {
    (storage || window.localStorage).setItem(companyAccessGreetedKey(userId, tenantId), JSON.stringify(capNewest(next)));
  } catch {
    /* best-effort local cache */
  }
}

/** Marks the state greeted (max, never `=`) and writes through to the server. */
export function markCompanyAccessGreeted(userId, tenantId, noticeKey, storage, nowMs = Date.now(), opts = {}) {
  if (!userId || !tenantId || !ownsCompanyAccessNoticeKey(noticeKey)) return;
  const current = readCompanyAccessGreeted(userId, tenantId, storage);
  const stamp = maxIso(current[noticeKey], new Date(nowMs).toISOString());
  writeMap(userId, tenantId, { ...current, [noticeKey]: stamp }, storage);
  if (!opts.skipSync && tenantIdForNoticeWrite(userId) === tenantId) {
    upsertNoticeReceipts({ tenantId, userId, rows: [{ notice_key: noticeKey, announced_at: stamp }] });
  }
  notify();
}

/** Server rows -> local map, MONOTONIC. */
export function mergeCompanyAccessGreetedFromServer(userId, tenantId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  let merged = readCompanyAccessGreeted(userId, tenantId, storage);
  let changed = false;
  rows.forEach((row) => {
    if (!ownsCompanyAccessNoticeKey(row?.notice_key) || !row.announced_at) return;
    if (!isNewerIso(row.announced_at, merged[row.notice_key])) return;
    merged = { ...merged, [row.notice_key]: row.announced_at };
    changed = true;
  });
  if (!changed) return;
  writeMap(userId, tenantId, merged, storage);
  notify();
}

/** Snapshot for the one-time post-hydration upload. */
export function collectCompanyAccessGreetedForUpload(userId, tenantId, storage) {
  const map = readCompanyAccessGreeted(userId, tenantId, storage);
  return Object.keys(map).map((k) => ({ notice_key: k, announced_at: map[k] }));
}
